
import { defineComponent, PropType, watch, ref, toRefs } from 'vue'
import { TabStructure } from '@/contracts/components/TabsContracts'
import { useRouter, useRoute } from 'vue-router'

export default defineComponent(
{
    name: 'Tabs',
    props:
    {
        tabs:
        {
            type: Array as PropType<Array<TabStructure>>,
            default: new Array<TabStructure>()
        }
    },
    setup(props)
    {
        // Load router/route:
        const router = useRouter()
        const route = useRoute()
        // Get hash from route:
        const hash = route.hash
        // Get tabs from props:
        const { tabs } = toRefs(props)
        // Set keys to listen for:
        const keys = { left: 'ArrowLeft', right: 'ArrowRight', up: 'ArrowUp', down: 'ArrowDown' }
        // Set class to hide panel:
        const jsHiddenClass = 'govuk-tabs__panel--hidden'
        // Media query boolean for mobile:
        const mediaQuery = window.matchMedia('(min-width: 40.0625em)').matches
        // Reactive current tab index:
        const currentTab = ref(0)
        // Set tab related attributes:
        let tabListRole: string|null = 'tablist'
        let tabListItemsRole: string|null = 'presentation'
        let tabRole: string|null = 'tab'
        let panelRole: string|null = 'tabpanel'

        // Check media query for mobile;
        if(typeof window.matchMedia === 'function')
        {
            // Remove attributes on mobile:
            if(!mediaQuery)
            {
                tabListRole = null
                tabListItemsRole = null
                tabRole = null
                panelRole = null
            }
        }

        // Check if hash exists on load:
        if(hash)
        {
            // Loop tabs and look for hash match:
            for(let i = 0; i < tabs.value.length; i++)
            {
                // On hash match display tab:
                if('#' + tabs.value[i].id === hash)
                {
                    currentTab.value = i
                    break
                }
            }
        }

        // Watch changes to current tab:
        watch(currentTab, function(newTab: number)
        {
            // Push hash of new tab:
            const id = tabs.value[newTab].id
            router.push({ hash: '#' + id, params: { noScroll: 1 } })
        })

        // Get tab id:
        const getTabId = function(tab: TabStructure): string|null
        {
            // Remove on mobile:
            if(!mediaQuery)
            {
                return null
            }
            return 'tab_' + tab.id
        }
        // Get aria controls value:
        const getAriaControls = function(tab: TabStructure): string|null
        {
            // Remove on mobile:
            if(!mediaQuery)
            {
                return null
            }
            return tab.id
        }
        // Get aria selected value:
        const getAriaSelected = function(index: number): boolean|null
        {
            // Remove on mobile:
            if(!mediaQuery)
            {
                return null
            }
            return currentTab.value === index
        }
        // Get tab index
        const getTabIndex = function(index: number): string|null
        {
            // Remove on mobile:
            if(!mediaQuery)
            {
                return null
            }
            return (index === currentTab.value ? '0' : '-1')
        }
        // Get tab list item classes:
        const tabListItemClassList = function(index: number): string
        {
            return 'govuk-tabs__list-item ' + (index === currentTab.value ? 'govuk-tabs__list-item--selected' : '')
        }
        // Get panel classes:
        const panelClassList = function(index: number): string
        {
            // Remove on mobile:
            if(!mediaQuery)
            {
                return 'govuk-tabs__panel'
            }
            return 'govuk-tabs__panel ' + (index === currentTab.value ? '' : jsHiddenClass)
        }
        // On tab click:
        const onTabClick = function(index: number): void
        {
            // Remove on mobile:
            if(!mediaQuery)
            {
                return
            }
            currentTab.value = index
        }
        // On tab key down:
        const onKeydown = function(e: KeyboardEvent): void
        {
            // Remove on mobile:
            if(!mediaQuery)
            {
                return
            }
            // Switch key and move tabs as required:
            switch(e.key)
            {
                case keys.left:
                case keys.up:
                    activatePreviousTab()
                    break
                case keys.right:
                case keys.down:
                    activateNextTab()
                    break
            }
        }
        // Move to previous tab:
        const activatePreviousTab = function(): void
        {
            if(currentTab.value > 0)
            {
                currentTab.value--
            }
        }
        // Move to next tab:
        const activateNextTab = function(): void
        {
            if(currentTab.value <= tabs.value.length)
            {
                currentTab.value++
            }
        }

        return {
            tabListRole,
            tabListItemsRole,
            tabRole,
            panelRole,
            getTabId,
            getAriaControls,
            getAriaSelected,
            getTabIndex,
            tabListItemClassList,
            panelClassList,
            onTabClick,
            onKeydown
        }
    }
})
